import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  BookOpen, 
  Globe, 
  Plug, 
  Shield, 
  Users, 
  CheckCircle, 
  ExternalLink,
  Download,
  FileText,
  Play,
  ArrowRight,
  ArrowLeft
} from "lucide-react";
import jsPDF from 'jspdf';
import { toast } from "sonner";

export default function IntegrationsGuide() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const generatePDF = () => {
    toast.promise(
      new Promise((resolve) => {
        setTimeout(() => {
          const doc = new jsPDF();
          
          // Set up colors
          const primaryColor = [59, 130, 246]; // Blue
          const textColor = [51, 51, 51]; // Dark gray
          const lightGray = [156, 163, 175];
          
          // Title Page
          doc.setFillColor(59, 130, 246);
          doc.rect(0, 0, 210, 40, 'F');
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(28);
          doc.setFont('helvetica', 'bold');
          doc.text('Edugogy Integration Guide', 105, 25, { align: 'center' });
          
          doc.setFontSize(14);
          doc.setFont('helvetica', 'normal');
          doc.text('Comprehensive Professional Development Platform', 105, 35, { align: 'center' });
          
          // Reset color for content
          doc.setTextColor(51, 51, 51);
          
          // Table of Contents
          let yPos = 60;
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text('Table of Contents', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          
          const tableOfContents = [
            '1. Overview & Benefits',
            '2. Technical Requirements',
            '3. Available Integrations',
            '4. Student Information Systems',
            '5. Learning Management Systems',
            '6. Authentication & Security',
            '7. Step-by-Step Implementation',
            '8. Troubleshooting & Support'
          ];
          
          tableOfContents.forEach((item, index) => {
            doc.text(item, 25, yPos);
            doc.text(`${index + 3}`, 190, yPos, { align: 'right' });
            yPos += 8;
          });
          
          // Page 2 - Overview & Benefits
          doc.addPage();
          yPos = 30;
          
          // Header
          doc.setFillColor(59, 130, 246);
          doc.rect(0, 0, 210, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('1. Overview & Benefits', 20, 13);
          
          // Content
          doc.setTextColor(51, 51, 51);
          yPos = 40;
          
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text('Why Integrate with Edugogy?', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const benefits = [
            'Unified User Management: Automatically sync staff from your SIS and HR systems',
            'Single Sign-On: Leverage existing Active Directory credentials for seamless access',
            'LMS Integration: Connect with Canvas, Moodle, Google Classroom, and more',
            'Modern & Secure: Built with enterprise security and compliance standards',
            'Automated Workflows: Streamline course enrollment and progress tracking',
            'Real-time Analytics: Generate comprehensive reports and insights'
          ];
          
          benefits.forEach((benefit) => {
            const splitText = doc.splitTextToSize(benefit, 170);
            doc.text(splitText, 20, yPos);
            yPos += splitText.length * 6 + 5;
          });
          
          // Key Features Box
          yPos += 10;
          doc.setFillColor(245, 245, 245);
          doc.rect(15, yPos - 5, 180, 60, 'F');
          
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('Key Integration Features:', 20, yPos + 5);
          
          yPos += 15;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          
          const keyFeatures = [
            '• RESTful API with comprehensive documentation',
            '• OAuth 2.0 and SAML 2.0 authentication support',
            '• Real-time data synchronization capabilities',
            '• Flexible webhook system for custom integrations',
            '• Enterprise-grade security and compliance (SOC 2, FERPA)',
            '• 24/7 technical support and implementation assistance'
          ];
          
          keyFeatures.forEach((feature) => {
            doc.text(feature, 25, yPos);
            yPos += 7;
          });
          
          // Page 3 - Technical Requirements
          doc.addPage();
          
          // Header
          doc.setFillColor(59, 130, 246);
          doc.rect(0, 0, 210, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('2. Technical Requirements', 20, 13);
          
          doc.setTextColor(51, 51, 51);
          yPos = 40;
          
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text('System Requirements', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('Browser Compatibility:', 20, yPos);
          
          yPos += 10;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const browserReqs = [
            '• Chrome 90+ (Recommended)',
            '• Firefox 88+',
            '• Safari 14+',
            '• Edge 90+',
            '• Mobile browsers supported'
          ];
          
          browserReqs.forEach((req) => {
            doc.text(req, 25, yPos);
            yPos += 7;
          });
          
          yPos += 10;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('Network Requirements:', 20, yPos);
          
          yPos += 10;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const networkReqs = [
            '• HTTPS connection required',
            '• Firewall ports: 443 (HTTPS), 80 (HTTP redirect)',
            '• Outbound connections to api.edugogy.com',
            '• Minimum bandwidth: 1 Mbps per concurrent user'
          ];
          
          networkReqs.forEach((req) => {
            doc.text(req, 25, yPos);
            yPos += 7;
          });
          
          // Page 4 - Available Integrations
          doc.addPage();
          
          // Header
          doc.setFillColor(59, 130, 246);
          doc.rect(0, 0, 210, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('3. Available Integrations', 20, 13);
          
          doc.setTextColor(51, 51, 51);
          yPos = 40;
          
          // Student Information Systems
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('Student Information Systems', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const sisIntegrations = [
            'Synergy SIS - Full user import and roster management',
            'PowerSchool - Real-time enrollment synchronization', 
            'Infinite Campus - Staff directory integration',
            'Skyward - Automated user provisioning'
          ];
          
          sisIntegrations.forEach((integration) => {
            const splitText = doc.splitTextToSize(`• ${integration}`, 170);
            doc.text(splitText, 25, yPos);
            yPos += splitText.length * 6 + 3;
          });
          
          yPos += 10;
          
          // Learning Management Systems
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('Learning Management Systems', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const lmsIntegrations = [
            'Canvas LMS - Course enrollment and grade passback',
            'Google Classroom - Assignment and roster sync',
            'Moodle - Single sign-on and user management',
            'Schoology - Content sharing and progress tracking'
          ];
          
          lmsIntegrations.forEach((integration) => {
            const splitText = doc.splitTextToSize(`• ${integration}`, 170);
            doc.text(splitText, 25, yPos);
            yPos += splitText.length * 6 + 3;
          });
          
          yPos += 10;
          
          // HR Systems
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('HR & Employee Systems', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const hrIntegrations = [
            'Tyler Tech Employee Access (iVisions ESS) - Staff profile synchronization (Medium difficulty, 1 hour)',
            'iVisions Portal - Employee credential management (Easy difficulty, 30 min)',
            'Active Directory/LDAP - Authentication and user roles (Medium difficulty, 1-2 hours)',
            'Microsoft Azure AD - Enterprise SSO with MFA support (Medium difficulty, 1-2 hours)'
          ];
          
          hrIntegrations.forEach((integration) => {
            const splitText = doc.splitTextToSize(`• ${integration}`, 170);
            doc.text(splitText, 25, yPos);
            yPos += splitText.length * 6 + 3;
          });
          
          // Page 7 - Support & Contact Information
          doc.addPage();
          
          // Header
          doc.setFillColor(59, 130, 246);
          doc.rect(0, 0, 210, 20, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('6. Support & Contact Information', 20, 13);
          
          doc.setTextColor(51, 51, 51);
          yPos = 40;
          
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text('Technical Support', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.text('For integration support and assistance:', 20, yPos);
          
          yPos += 15;
          doc.setFont('helvetica', 'bold');
          doc.text('Email:', 20, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text('info@infusedu.com', 60, yPos);
          
          yPos += 12;
          doc.setFont('helvetica', 'bold');
          doc.text('Support Hours:', 20, yPos);
          doc.setFont('helvetica', 'normal');
          doc.text('Monday - Friday, 8 AM - 6 PM EST', 80, yPos);
          
          yPos += 20;
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('Additional Resources', 20, yPos);
          
          yPos += 15;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          
          const supportResources = [
            'Online Documentation Portal - Comprehensive guides and API references',
            'Video Training Library - Step-by-step integration tutorials',
            'Community Forum - Connect with other administrators and experts',
            'Implementation Services - Professional services for complex deployments'
          ];
          
          supportResources.forEach((resource) => {
            const splitText = doc.splitTextToSize(`• ${resource}`, 170);
            doc.text(splitText, 25, yPos);
            yPos += splitText.length * 6 + 3;
          });
          
          const implementationSteps = [
            {
              title: 'Phase 1: Requirements Assessment (1-2 weeks)',
              items: [
                'Technical infrastructure review',
                'Security and compliance audit',
                'Stakeholder interviews and needs analysis',
                'Integration roadmap development'
              ]
            },
            {
              title: 'Phase 2: Authentication Setup (1 week)',
              items: [
                'Active Directory/LDAP configuration',
                'SAML 2.0 integration setup',
                'Multi-factor authentication enablement',
                'User testing and validation'
              ]
            },
            {
              title: 'Phase 3: Data Integration (2-3 weeks)',
              items: [
                'SIS connection and data mapping',
                'User import and profile synchronization',
                'LMS integration and course setup',
                'Data validation and cleanup'
              ]
            },
            {
              title: 'Phase 4: Testing & Go-Live (1 week)',
              items: [
                'End-to-end integration testing',
                'User acceptance testing',
                'Staff training and documentation',
                'Production deployment and monitoring'
              ]
            }
          ];
          
          implementationSteps.forEach((phase) => {
            if (yPos > 250) {
              doc.addPage();
              yPos = 30;
            }
            
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(phase.title, 20, yPos);
            
            yPos += 12;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            phase.items.forEach((item) => {
              const splitText = doc.splitTextToSize(`• ${item}`, 170);
              doc.text(splitText, 25, yPos);
              yPos += splitText.length * 5 + 3;
            });
            
            yPos += 8;
          });
          
          // Footer on every page
          const totalPages = doc.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            
            // Footer line
            doc.setDrawColor(156, 163, 175);
            doc.line(20, 285, 190, 285);
            
            // Footer text
            doc.setFontSize(9);
            doc.setTextColor(156, 163, 175);
            doc.setFont('helvetica', 'normal');
            doc.text('Edugogy Integration Guide', 20, 292);
            doc.text(`Page ${i} of ${totalPages}`, 190, 292, { align: 'right' });
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 292, { align: 'center' });
          }
          
          // Save the PDF
          doc.save('edugogy-integration-guide.pdf');
          resolve(true);
        }, 1500);
      }),
      {
        loading: 'Generating comprehensive integration guide...',
        success: 'PDF guide downloaded successfully!',
        error: 'Failed to generate PDF guide'
      }
    );
  };

  const integrationSteps = [
    {
      title: "Requirements Assessment",
      description: "Verify your system meets all technical requirements",
      icon: CheckCircle,
      content: [
        "Modern web browser compatibility check",
        "Network connectivity requirements",
        "Administrator access permissions",
        "Domain and SSL certificate setup"
      ]
    },
    {
      title: "Authentication Setup",
      description: "Configure Single Sign-On and directory services",
      icon: Shield,
      content: [
        "Active Directory/LDAP configuration",
        "SAML 2.0 integration setup",
        "Multi-Factor Authentication enablement",
        "User role mapping and permissions"
      ]
    },
    {
      title: "Data Integration",
      description: "Connect your Student Information Systems",
      icon: Users,
      content: [
        "Synergy SIS connection setup",
        "iVisions portal integration",
        "Tyler Tech Employee Access sync",
        "Field mapping and data validation"
      ]
    },
    {
      title: "LMS Integration",
      description: "Connect with Learning Management Systems",
      icon: Plug,
      content: [
        "Canvas LMS API configuration",
        "Moodle integration setup",
        "Google Classroom connection",
        "Grade passback and enrollment sync"
      ]
    }
  ];

  const integrations = [
    {
      name: "Canvas LMS",
      category: "Learning Management System",
      description: "Seamless course enrollment and certificate delivery",
      features: [
        "Automatic course enrollment sync",
        "Grade passback integration",
        "Certificate posting to Canvas",
        "Deep linking support"
      ],
      difficulty: "Easy",
      timeEstimate: "30-45 minutes",
      prerequisites: ["Canvas Admin Access", "API Key"],
      status: "Available"
    },
    {
      name: "Microsoft Azure AD",
      category: "Identity Provider",
      description: "Enterprise single sign-on with MFA support",
      features: [
        "SAML 2.0 authentication",
        "Multi-Factor Authentication",
        "Group-based role assignment",
        "Just-in-time provisioning"
      ],
      difficulty: "Medium",
      timeEstimate: "1-2 hours",
      prerequisites: ["Azure AD Admin", "SAML App Registration"],
      status: "Available"
    },
    {
      name: "Synergy SIS",
      category: "Student Information System",
      description: "Staff directory and organizational data sync",
      features: [
        "Staff directory synchronization",
        "Department and position mapping",
        "Automated user creation",
        "Scheduled data updates"
      ],
      difficulty: "Medium",
      timeEstimate: "45-60 minutes",
      prerequisites: ["Database Access", "IT Coordination"],
      status: "Available"
    },
    {
      name: "Tyler Tech Employee Access",
      category: "HR Management System",
      description: "Employee data and credentials integration",
      subtitle: "iVisions ESS",
      features: [
        "Employee profile synchronization",
        "Credential and certification tracking", 
        "Professional development history",
        "Automated role assignments"
      ],
      difficulty: "Medium",
      timeEstimate: "1 hour",
      prerequisites: ["Tyler Tech API Access", "Admin Permissions"],
      status: "Available"
    },
    {
      name: "iVisions Portal",
      category: "Employee Access Portal",
      description: "Employee access portal integration",
      features: [
        "Employee profile import",
        "Access credential mapping",
        "Bulk user provisioning",
        "Real-time data sync"
      ],
      difficulty: "Easy",
      timeEstimate: "30 minutes",
      prerequisites: ["API Endpoint", "Authentication Credentials"],
      status: "Available"
    },
    {
      name: "Google Classroom",
      category: "Learning Management System",
      description: "Integration with Google's educational platform",
      features: [
        "Course roster synchronization",
        "Assignment integration",
        "Google Drive file sharing",
        "OAuth 2.0 authentication"
      ],
      difficulty: "Easy",
      timeEstimate: "20-30 minutes",
      prerequisites: ["Google Workspace Admin", "API Credentials"],
      status: "Available"
    }
  ];

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Dashboard
        </Button>
        <h1 className="text-3xl font-bold text-center flex-1">Edugogy Integration Guide</h1>
        <div className="w-[140px]"></div> {/* Spacer for centering */}
      </div>
      
      {/* Description */}
      <div className="text-center space-y-4">
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Comprehensive guide to integrating Edugogy with your district's existing systems for seamless professional development management
        </p>
        <div className="flex items-center justify-center gap-4 mt-6">
          <Badge variant="secondary" className="px-4 py-2">
            <CheckCircle className="h-4 w-4 mr-2" />
            6 Available Integrations
          </Badge>
          <Badge variant="outline" className="px-4 py-2">
            <Globe className="h-4 w-4 mr-2" />
            Enterprise Ready
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-8">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="quick-start">Quick Start</TabsTrigger>
          <TabsTrigger value="integrations">Available Integrations</TabsTrigger>
          <TabsTrigger value="step-by-step">Step-by-Step</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card className="bg-gradient-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Why Integrate with Edugogy?</CardTitle>
              <CardDescription className="text-lg">
                Streamline your professional development ecosystem with seamless data flow
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Users className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Unified User Management</h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync staff from your SIS and HR systems
                  </p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Single Sign-On</h3>
                  <p className="text-sm text-muted-foreground">
                    Leverage existing Active Directory credentials
                  </p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Plug className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">LMS Integration</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect with Canvas, Moodle, and Google Classroom
                  </p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Globe className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Modern & Secure</h3>
                  <p className="text-sm text-muted-foreground">
                    Built with enterprise security and compliance
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5 text-primary" />
                  Getting Started
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  New to Edugogy integrations? Start here for a guided setup experience.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.location.href = '/tech-requirements-status'}
                >
                  Start Setup Wizard
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Documentation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Detailed technical documentation for all integrations.
                </p>
                <Button variant="outline" className="w-full" onClick={generatePDF}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF Guide
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="h-5 w-5 text-primary" />
                  Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Need help? Our integration specialists are ready to assist.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.location.href = 'mailto:info@infusedu.com'}
                >
                  Contact Support
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="quick-start" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Start Checklist</CardTitle>
              <CardDescription>
                Complete these steps to get up and running with Edugogy integrations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  "Verify system requirements and browser compatibility",
                  "Gather admin credentials for your systems (SIS, AD, LMS)",
                  "Configure Single Sign-On with your identity provider",
                  "Set up your primary data source (SIS or HR system)",
                  "Connect your Learning Management System",
                  "Test integrations and user workflows",
                  "Train administrators and end users"
                ].map((step, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">{index + 1}</span>
                    </div>
                    <span className="text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Estimated Setup Time:</strong> Most districts complete the full integration process in 2-4 hours, spread across multiple sessions with their IT team.
            </AlertDescription>
          </Alert>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6">
          <div className="grid gap-6">
            {integrations.map((integration, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-xl">
                            {integration.name}
                            {integration.subtitle && (
                              <span className="text-sm font-normal text-muted-foreground ml-2">
                                ({integration.subtitle})
                              </span>
                            )}
                          </CardTitle>
                          <Badge variant="outline">{integration.category}</Badge>
                        <Badge variant={
                          integration.difficulty === 'Easy' ? 'secondary' :
                          integration.difficulty === 'Medium' ? 'default' : 'destructive'
                        }>
                          {integration.difficulty}
                        </Badge>
                      </div>
                      <CardDescription className="text-base">
                        {integration.description}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="ml-4">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {integration.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3">Key Features</h4>
                      <ul className="space-y-2">
                        {integration.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Setup Information</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Time Estimate:</span>
                            <span className="font-medium">{integration.timeEstimate}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Difficulty:</span>
                            <span className="font-medium">{integration.difficulty}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Prerequisites</h4>
                        <div className="flex flex-wrap gap-1">
                          {integration.prerequisites.map((prereq, prereqIndex) => (
                            <Badge key={prereqIndex} variant="outline" className="text-xs">
                              {prereq}
                            </Badge>
                          ))}
                        </div>
                      </div>
                <Button 
                  className="w-full"
                  onClick={() => {
                    const routes = {
                      'Canvas LMS': '/api-integrations',
                      'Microsoft Azure AD': '/sso-configuration', 
                      'Synergy SIS': '/sis-configuration',
                      'Tyler Tech Employee Access': '/tyler-tech-integration',
                      'iVisions Portal': '/sis-configuration',
                      'Google Classroom': '/api-integrations'
                    };
                    window.location.href = routes[integration.name as keyof typeof routes] || '/api-integrations';
                  }}
                >
                  Configure {integration.name}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="step-by-step" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Integration Workflow</CardTitle>
              <CardDescription>
                Follow this step-by-step process for a successful integration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {integrationSteps.map((step, index) => (
                  <div 
                    key={index}
                    className={`flex gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                      activeStep === index ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setActiveStep(index)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activeStep === index ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      <step.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Step {index + 1}: {step.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{step.description}</p>
                      {activeStep === index && (
                        <ul className="space-y-1">
                          {step.content.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-3 w-3 text-success" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                      <Button 
                        onClick={() => {
                          const routes = {
                            0: '/tech-requirements-status',
                            1: '/sso-configuration',
                            2: '/sis-configuration', 
                            3: '/api-integrations'
                          };
                          window.location.href = routes[index as keyof typeof routes] || '/tech-requirements';
                        }}
                      >
                        Start Step
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}